// src/components/roads/AIOperationsPanel.js
// --- UPDATE (v2.0 AI-connected command center)
//   • Model status: ML circuit state (healthy / recovering / offline) + last refresh.
//   • Predictive RUL: band distribution of the loaded roads, ML coverage
//     (how many have a real model prediction vs estimate), "Run RUL prediction".
//   • Top-risk roads ranked by the AI risk index — click to open the road.
//   • Data readiness: average readiness, AI-ready count, which models are blocked
//     and how many roads are unmapped — the levers that improve predictions.
//   • Collapsible (it floats over the map) and explained with FieldHint popovers.
//   • All new props are optional; the original props behave exactly as before.
//   • FIX: averageRUL arrives as a string from toFixed() — clamp + Number() for the ring.
// --- END UPDATE
// --- UPDATE (Drive Mode AI review): road-damage detection model (version, mAP50, labelled
// photos, Train) between clustering and Predictive RUL.
// --- END UPDATE
// --- UPDATE (roads v1.4): "Rain water forecast" section — segments / roads that will hold water, deepest, when it
// starts, and buttons to show it on the map / open the forecast card. Optional props (pondingSummary, onShowPonding).
// --- END UPDATE
import React, { useState, useMemo } from 'react'; // --- UPDATE: + useMemo
import {
    Box, VStack, HStack, Text, Button, Icon, Badge,
    useColorModeValue, Divider, Tooltip, CircularProgress, CircularProgressLabel,
    // --- UPDATE
    Flex, IconButton, Collapse, Progress,
    // --- END UPDATE
} from '@chakra-ui/react';
import { MdAutoAwesome, MdLayers, MdPolyline, MdWarning, // eslint-disable-line no-unused-vars
    // --- UPDATE
    MdExpandLess, MdExpandMore, MdTimeline, MdChevronRight, MdFiberManualRecord, MdBlock, MdLocationOff,
    // --- END UPDATE
} from 'react-icons/md';
import { FaBrain, FaRoad } from 'react-icons/fa';
import { motion } from 'framer-motion';
// --- UPDATE
import { FieldHint, AI_MODELS } from './AiRoadHelper';
import { summarizeNetwork, RUL_BANDS } from './roadAiPredictions';
// --- END UPDATE
import RoadVisionModelPanel from './drive/RoadVisionModelPanel'; // --- UPDATE (Drive Mode AI review)

const MotionBox = motion(Box);

// --- UPDATE: ML service state shown in the header
const CIRCUIT_STATE = {
    CLOSED: { label: 'ML online', color: 'green' },
    HALF_OPEN: { label: 'ML recovering', color: 'yellow' },
    OPEN: { label: 'ML offline', color: 'red' },
};
const fmtTime = (d) => {
    if (!d) return null;
    const t = d instanceof Date ? d : new Date(d);
    return Number.isFinite(t.getTime()) ? t.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : null;
};
// --- END UPDATE

const AIOperationsPanel = ({
    unclusteredCount = 0,
    onRunClustering,
    isClustering,
    heatmapActive,
    onToggleHeatmap,
    averageRUL = 0,
    // --- UPDATE: optional AI wiring
    roads,
    onRunPrediction,
    isPredicting = false,
    aiCircuit,
    lastRefresh,
    onSelectRoad,
    unmappedCount,
    defaultCollapsed = false,
    // --- END UPDATE
    // --- UPDATE (roads v1.4)
    pondingSummary = null,
    onShowPonding,
    // --- END UPDATE
}) => {
    const bgColor = useColorModeValue('whiteAlpha.900', 'blackAlpha.800');
    const borderColor = useColorModeValue('whiteAlpha.500', 'whiteAlpha.200');
    const panelShadow = useColorModeValue('0 8px 32px rgba(0,0,0,0.1)', '0 8px 32px rgba(0,0,0,0.4)');
    // --- UPDATE
    const muted = useColorModeValue('gray.500', 'gray.400');
    const rowHover = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const network = useMemo(() => (Array.isArray(roads) ? summarizeNetwork(roads) : null), [roads]);
    const avgRulNum = Number(network?.averageRUL ?? averageRUL) || 0;
    const circuit = CIRCUIT_STATE[String(aiCircuit || '').toUpperCase()] || (aiCircuit ? { label: `ML ${String(aiCircuit).toLowerCase()}`, color: 'gray' } : null);
    const unmapped = unmappedCount ?? network?.unmapped;
    // --- END UPDATE

    return (
        <MotionBox
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            position="absolute"
            top="20px"
            right="20px"
            w="320px"
            bg={bgColor}
            backdropFilter="blur(16px)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow={panelShadow}
            overflow="hidden"
            zIndex={1000} // Ensures it floats above the Leaflet map
            // --- UPDATE: scroll inside on short screens
            maxH="calc(100% - 40px)"
            overflowY="auto"
            role="region"
            aria-label="AI command center"
            // --- END UPDATE
        >
            {/* Header Gradient */}
            <Box bgGradient="linear(to-r, purple.500, blue.500)" p={3}>
                <HStack spacing={2}>
                    <Icon as={FaBrain} color="white" boxSize={5} />
                    <Text color="white" fontWeight="bold" fontSize="md">AI Command Center</Text>
                    {/* --- UPDATE: model status + collapse */}
                    {circuit && (
                        <Tooltip label={`Road AI engine circuit: ${aiCircuit}${fmtTime(lastRefresh) ? ` · data refreshed ${fmtTime(lastRefresh)}` : ''}`} hasArrow>
                            <Badge ml="auto" colorScheme={circuit.color} variant="solid" borderRadius="full" fontSize="9px" display="flex" alignItems="center" gap={1} cursor="help">
                                <Icon as={MdFiberManualRecord} boxSize={2} />{circuit.label}
                            </Badge>
                        </Tooltip>
                    )}
                    <IconButton size="xs" variant="ghost" color="white" _hover={{ bg: 'whiteAlpha.300' }}
                        ml={circuit ? 0 : 'auto'}
                        aria-label={collapsed ? 'Expand AI command center' : 'Collapse AI command center'}
                        icon={<Icon as={collapsed ? MdExpandMore : MdExpandLess} />}
                        onClick={() => setCollapsed(c => !c)} />
                    {/* --- END UPDATE */}
                </HStack>
            </Box>

            {/* --- UPDATE: collapsible body */}
            <Collapse in={!collapsed} animateOpacity>
            {/* --- END UPDATE */}
            <VStack align="stretch" spacing={4} p={4}>
                {/* Clustering Section */}
                <Box>
                    <HStack justify="space-between" mb={2}>
                        {/* --- UPDATE: explanation */}
                        <HStack spacing={0}>
                            <Text fontSize="sm" fontWeight="bold" color="gray.500">Anomaly Clustering</Text>
                            <FieldHint metric="ai_dispatch" />
                        </HStack>
                        {/* --- END UPDATE */}
                        <Badge colorScheme={unclusteredCount > 0 ? 'red' : 'green'} variant="subtle" borderRadius="full">
                            {unclusteredCount} Pending
                        </Badge>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                        Group raw dashcam detections into dispatchable work orders.
                    </Text>
                    <Tooltip label={unclusteredCount === 0 ? "No anomalies to cluster" : "Run geospatial clustering algorithm"} hasArrow>
                        <Button
                            w="full"
                            colorScheme="purple"
                            size="sm"
                            leftIcon={<Icon as={MdAutoAwesome} />}
                            isLoading={isClustering}
                            loadingText="Clustering..."
                            isDisabled={unclusteredCount === 0}
                            onClick={onRunClustering}
                            boxShadow="md"
                        >
                            Run AI Clustering
                        </Button>
                    </Tooltip>
                </Box>

                {/* --- UPDATE (roads v1.4): rain-water forecast */}
                {pondingSummary && (
                    <>
                        <Divider />
                        <Box>
                            <HStack justify="space-between" mb={1}>
                                <Text fontSize="sm" fontWeight="bold" color="gray.500">Rain water forecast</Text>
                                <Badge colorScheme={pondingSummary.count ? (pondingSummary.scheme || 'blue') : 'green'} variant="subtle" borderRadius="full">
                                    {pondingSummary.count ? `${pondingSummary.count} wet` : 'Clear'}
                                </Badge>
                            </HStack>
                            <Text fontSize="xs" color="gray.500" mb={2}>
                                {pondingSummary.count
                                    ? `${pondingSummary.roads} road(s) · deepest ${pondingSummary.maxDepth}${pondingSummary.starts ? ` · starts ${pondingSummary.starts}` : ''} — live rainfall × drainage × terrain.`
                                    : 'No standing water forecast on the mapped roads for the next 24 h.'}
                            </Text>
                            {onShowPonding && pondingSummary.count > 0 && (
                                <Button w="full" size="sm" colorScheme="blue" variant="outline" leftIcon={<Icon as={MdLayers} />} onClick={onShowPonding}>
                                    Show where water settles
                                </Button>
                            )}
                        </Box>
                    </>
                )}
                {/* --- END UPDATE */}

                <Divider />

                {/* --- UPDATE (Drive Mode AI review): detection model */}
                <RoadVisionModelPanel variant="mini" />
                <Divider />
                {/* --- END UPDATE */}

                {/* Predictive RUL Section */}
                <Box>
                    <HStack justify="space-between" mb={2}>
                        {/* --- UPDATE */}
                        <HStack spacing={0}>
                            <Text fontSize="sm" fontWeight="bold" color="gray.500">Predictive RUL</Text>
                            <FieldHint metric="rul" />
                        </HStack>
                        <Badge colorScheme={circuit?.color === 'red' ? 'red' : 'blue'} variant="subtle" borderRadius="full">
                            {circuit?.color === 'red' ? 'Paused' : 'Active'}
                        </Badge>
                        {/* --- END UPDATE */}
                    </HStack>

                    <HStack justify="space-between" mb={3}>
                        <VStack align="start" spacing={0}>
                            <Text fontSize="xs" color="gray.500">Network Avg. Lifespan</Text>
                            <HStack>
                                <Text fontSize="xl" fontWeight="black" color="blue.500">{network?.averageRUL ?? averageRUL}</Text>
                                <Text fontSize="xs" color="gray.500" fontWeight="bold">YRS</Text>
                            </HStack>
                            {/* --- UPDATE: ML coverage */}
                            {network && (
                                <Tooltip hasArrow label={`${network.modelCount} of ${network.total} loaded roads have an ML prediction; ${network.estimateCount} use a rule-based estimate.`}>
                                    <Text fontSize="2xs" color={muted} cursor="help">{network.coverage}% ML coverage</Text>
                                </Tooltip>
                            )}
                            {/* --- END UPDATE */}
                        </VStack>
                        {/* --- UPDATE: clamp (string input, >20 yrs) */}
                        <CircularProgress value={Math.min(100, Math.max(0, (avgRulNum / 20) * 100))} color="blue.400" size="40px" thickness="12px">
                        {/* --- END UPDATE */}
                            <CircularProgressLabel fontSize="xs">
                                <Icon as={FaRoad} color="blue.500" />
                            </CircularProgressLabel>
                        </CircularProgress>
                    </HStack>

                    {/* --- UPDATE: band distribution */}
                    {network && network.total > 0 && (
                        <Box mb={3}>
                            <Flex h="8px" borderRadius="full" overflow="hidden" mb={1} aria-label="Remaining-life distribution">
                                {RUL_BANDS.map(b => network.bands[b.key] > 0 && (
                                    <Tooltip key={b.key} label={`${b.label}: ${network.bands[b.key]} road${network.bands[b.key] === 1 ? '' : 's'}`} hasArrow>
                                        <Box h="full" bg={`${b.color}.400`} flex={network.bands[b.key]} />
                                    </Tooltip>
                                ))}
                                {network.total - Object.values(network.bands).reduce((s, v) => s + v, 0) > 0 && (
                                    <Tooltip label="No prediction or estimate" hasArrow>
                                        <Box h="full" bg="gray.300" flex={network.total - Object.values(network.bands).reduce((s, v) => s + v, 0)} />
                                    </Tooltip>
                                )}
                            </Flex>
                            <HStack spacing={2} fontSize="2xs" color={muted} wrap="wrap">
                                {RUL_BANDS.map(b => (
                                    <HStack key={b.key} spacing={1}>
                                        <Box w={2} h={2} borderRadius="full" bg={`${b.color}.400`} />
                                        <Text>{b.label} {network.bands[b.key]}</Text>
                                    </HStack>
                                ))}
                            </HStack>
                        </Box>
                    )}
                    {onRunPrediction && (
                        <Tooltip hasArrow label="Re-score every road with the Random Survival Forest model using the latest condition, age and surface data.">
                            <Button w="full" size="sm" mb={2} colorScheme="purple" variant="outline"
                                leftIcon={<Icon as={MdTimeline} />} onClick={onRunPrediction}
                                isLoading={isPredicting} loadingText="Predicting…"
                                isDisabled={circuit?.color === 'red'}>
                                Run RUL prediction
                            </Button>
                        </Tooltip>
                    )}
                    {/* --- END UPDATE */}

                    <Button
                        w="full"
                        variant={heatmapActive ? "solid" : "outline"}
                        colorScheme="blue"
                        size="sm"
                        leftIcon={<Icon as={MdLayers} />}
                        onClick={onToggleHeatmap}
                    >
                        {heatmapActive ? "Hide RUL Heatmap" : "Show RUL Heatmap"}
                    </Button>
                </Box>

                {/* --- UPDATE: top-risk roads */}
                {network && network.topRisk.length > 0 && (
                    <>
                        <Divider />
                        <Box>
                            <HStack justify="space-between" mb={2}>
                                <HStack spacing={0}>
                                    <Text fontSize="sm" fontWeight="bold" color="gray.500">Highest AI risk</Text>
                                    <FieldHint metric="risk_index" />
                                </HStack>
                                <Text fontSize="2xs" color={muted}>loaded roads</Text>
                            </HStack>
                            <VStack align="stretch" spacing={1}>
                                {network.topRisk.slice(0, 3).map(({ road, prediction }) => (
                                    <HStack key={road.id} as={onSelectRoad ? 'button' : 'div'} type={onSelectRoad ? 'button' : undefined}
                                        onClick={onSelectRoad ? () => onSelectRoad(road) : undefined}
                                        spacing={2} p={1.5} borderRadius="md" _hover={onSelectRoad ? { bg: rowHover } : undefined}
                                        textAlign="left" w="full" aria-label={onSelectRoad ? `Open ${road.name}` : undefined}>
                                        <Badge colorScheme={prediction.riskLevel.color} variant="solid" minW="28px" textAlign="center">{prediction.riskScore}</Badge>
                                        <Box flex={1} minW={0}>
                                            <Text fontSize="xs" fontWeight="700" noOfLines={1}>{road.name}</Text>
                                            <Text fontSize="2xs" color={muted} noOfLines={1}>{prediction.actions[0]?.label}</Text>
                                        </Box>
                                        {onSelectRoad && <Icon as={MdChevronRight} color={muted} />}
                                    </HStack>
                                ))}
                            </VStack>
                        </Box>
                    </>
                )}

                {/* --- data readiness: what improves the AI */}
                {network && network.total > 0 && (
                    <>
                        <Divider />
                        <Box>
                            <HStack justify="space-between" mb={2}>
                                <HStack spacing={0}>
                                    <Text fontSize="sm" fontWeight="bold" color="gray.500">Data readiness</Text>
                                    <FieldHint metric="readiness" />
                                </HStack>
                                <Badge colorScheme={network.readinessAvg >= 85 ? 'green' : network.readinessAvg >= 60 ? 'yellow' : 'red'} borderRadius="full">
                                    {network.readinessAvg}%
                                </Badge>
                            </HStack>
                            <Progress value={network.readinessAvg} size="xs" borderRadius="full" mb={2}
                                colorScheme={network.readinessAvg >= 85 ? 'green' : network.readinessAvg >= 60 ? 'yellow' : 'red'} />
                            <Text fontSize="2xs" color={muted} mb={1.5}>
                                {network.aiReady} of {network.total} loaded roads are fully AI-ready.
                            </Text>
                            <HStack spacing={1} wrap="wrap">
                                {Object.entries(network.blocked).sort((a, b) => b[1] - a[1]).map(([key, count]) => (
                                    <Tooltip key={key} hasArrow label={`${AI_MODELS[key]?.label || key} can't run for ${count} road${count === 1 ? '' : 's'} — required data is missing. Open a road's AI helper to see what to add.`}>
                                        <Badge colorScheme="red" variant="subtle" fontSize="9px" display="flex" alignItems="center" gap={0.5} cursor="help">
                                            <Icon as={MdBlock} boxSize={2.5} />{AI_MODELS[key]?.short || key} {count}
                                        </Badge>
                                    </Tooltip>
                                ))}
                                {unmapped > 0 && (
                                    <Tooltip hasArrow label="Roads not drawn on the GIS map are excluded from the heatmap and pothole clustering.">
                                        <Badge colorScheme="orange" variant="subtle" fontSize="9px" display="flex" alignItems="center" gap={0.5} cursor="help">
                                            <Icon as={MdLocationOff} boxSize={2.5} />{unmapped} unmapped
                                        </Badge>
                                    </Tooltip>
                                )}
                            </HStack>
                        </Box>
                    </>
                )}
                {/* --- END UPDATE */}
            </VStack>
            {/* --- UPDATE */}
            </Collapse>
            {/* --- END UPDATE */}
        </MotionBox>
    );
};

export default AIOperationsPanel;